<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import {
		getNodeTypesByCategory,
		type NodeCategory,
		type NodeTypeConfig,
	} from '../core/config/node-types';

	/** When `connect`, editor-only nodes (e.g. note) are hidden — they cannot be wired. */
	export let placementPurpose: 'free' | 'connect' = 'free';
	export let x = 0;
	export let y = 0;
	export let visible = false;
	export let flowX = 0;
	export let flowY = 0;
	export let title = 'Add node';
	export let hint = 'Pick a node type or cancel.';

	const dispatch = createEventDispatcher<{
		pick: { type: string; paletteKey: string; x: number; y: number };
		cancel: void;
	}>();

	const categoryOrder: NodeCategory[] = ['conditions', 'logic', 'actions', 'specialCards'];

	let search = '';

	$: allOptions = (() => {
		const by = getNodeTypesByCategory();
		const out: NodeTypeConfig[] = [];
		for (const c of categoryOrder) {
			out.push(...by[c]);
		}
		if (placementPurpose === 'connect') {
			return out.filter((n) => n.type !== 'note');
		}
		return out;
	})();

	$: filtered = (() => {
		const q = search.trim().toLowerCase();
		if (!q) return allOptions;
		return allOptions.filter((n) => {
			const label = n.label.toLowerCase();
			const desc = (n.description ?? '').toLowerCase();
			const type = String(n.type).toLowerCase();
			const pk = String(n.paletteKey ?? '').toLowerCase();
			return label.includes(q) || desc.includes(q) || type.includes(q) || pk.includes(q);
		});
	})();

	function pickNode(entry: NodeTypeConfig) {
		const paletteKey = entry.paletteKey ?? entry.type;
		dispatch('pick', { type: entry.type, paletteKey, x: flowX, y: flowY });
		visible = false;
		search = '';
	}

	function cancel() {
		dispatch('cancel');
		visible = false;
		search = '';
	}
</script>

{#if visible}
	<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
	<div class="qn-backdrop" on:click={cancel} role="presentation" />
	<div class="qn-menu" style="left:{x}px; top:{y}px;" role="dialog" aria-label={title}>
		<div class="qn-header-row">
			<span class="qn-header-title">{title}</span>
			<button type="button" class="qn-close" on:click={cancel} aria-label="Close">
				<i class="fa fa-times" />
			</button>
		</div>
		<p class="qn-hint">{hint}</p>
		<div class="qn-search-wrap">
			<i class="fa fa-search qn-search-icon" aria-hidden="true" />
			<input
				type="search"
				class="qn-search"
				placeholder="Search nodes…"
				bind:value={search}
				autocomplete="off"
				spellcheck="false"
			/>
		</div>
		<div class="qn-divider" />
		<div class="qn-subhead">Quick nodes</div>
		<div class="qn-list">
			{#if filtered.length === 0}
				<p class="qn-empty">No nodes match “{search.trim()}”.</p>
			{:else}
				{#each filtered as opt (opt.paletteKey ?? opt.type + opt.label)}
					<button type="button" class="qn-item" on:click={() => pickNode(opt)}>
						<i class="fa {opt.icon}" style="color:{opt.color}" />
						<span class="qn-item-label">{opt.label}</span>
					</button>
				{/each}
			{/if}
		</div>
		<div class="qn-divider" />
		<button type="button" class="qn-cancel-bottom" on:click={cancel}>Cancel</button>
	</div>
{/if}

<style>
	.qn-backdrop {
		position: fixed;
		inset: 0;
		z-index: 12000;
		background: rgba(0, 0, 0, 0.2);
	}

	.qn-menu {
		position: fixed;
		z-index: 12001;
		background: #2a2a2a;
		border: 1px solid #3a3a3a;
		border-radius: 8px;
		min-width: 260px;
		max-width: min(360px, calc(100vw - 24px));
		max-height: min(480px, 72vh);
		display: flex;
		flex-direction: column;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
	}

	.qn-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		padding: 0.45rem 0.5rem 0.45rem 0.75rem;
		border-bottom: 1px solid #3a3a3a;
		flex-shrink: 0;
	}

	.qn-header-title {
		font-size: 0.7rem;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #888;
	}

	.qn-close {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		padding: 0;
		border: none;
		border-radius: 6px;
		background: transparent;
		color: #9ca3af;
		cursor: pointer;
		font-size: 0.85rem;
		flex-shrink: 0;
	}

	.qn-close:hover {
		background: #3a3a3a;
		color: #e5e5e5;
	}

	.qn-hint {
		margin: 0;
		padding: 0.5rem 0.75rem 0.35rem;
		font-size: 0.75rem;
		line-height: 1.35;
		color: #aaa;
		flex-shrink: 0;
	}

	.qn-search-wrap {
		position: relative;
		margin: 0 0.75rem 0.5rem;
		flex-shrink: 0;
	}

	.qn-search-icon {
		position: absolute;
		left: 10px;
		top: 50%;
		transform: translateY(-50%);
		font-size: 0.72rem;
		color: #6b7280;
		pointer-events: none;
	}

	.qn-search {
		width: 100%;
		box-sizing: border-box;
		padding: 0.45rem 0.55rem 0.45rem 2rem;
		border-radius: 6px;
		border: 1px solid #4a4a4a;
		background: #1f1f1f;
		color: #e5e5e5;
		font-size: 0.82rem;
		outline: none;
	}

	.qn-search:focus {
		border-color: #6366f1;
	}

	.qn-divider {
		height: 1px;
		background: #3a3a3a;
		margin: 0;
		flex-shrink: 0;
	}

	.qn-subhead {
		padding: 0.35rem 0.75rem;
		font-size: 0.65rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #777;
		flex-shrink: 0;
	}

	.qn-list {
		overflow-y: auto;
		flex: 1;
		min-height: 0;
	}

	.qn-empty {
		margin: 0;
		padding: 0.75rem;
		font-size: 0.8rem;
		color: #9ca3af;
	}

	.qn-item {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		width: 100%;
		padding: 0.55rem 0.75rem;
		background: transparent;
		border: none;
		color: #e5e5e5;
		font-size: 0.85rem;
		cursor: pointer;
		text-align: left;
	}

	.qn-item:hover {
		background: #3a3a3a;
	}

	.qn-item i {
		width: 16px;
		text-align: center;
		font-size: 0.8rem;
		flex-shrink: 0;
	}

	.qn-item-label {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.qn-cancel-bottom {
		display: block;
		width: calc(100% - 1rem);
		margin: 0.5rem;
		padding: 0.5rem 0.65rem;
		background: #3a3a3a;
		border: 1px solid #4a4a4a;
		border-radius: 6px;
		color: #e5e5e5;
		font-size: 0.85rem;
		cursor: pointer;
		flex-shrink: 0;
	}

	.qn-cancel-bottom:hover {
		background: #454545;
	}
</style>
